import {
  Box,
  Button,
  Combobox,
  createListCollection,
  Heading,
  HStack,
  Input,
  NativeSelect,
  Portal,
  Separator,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  EXTENDED_TIMEZONE_OPTIONS,
  normalizeTimeZone,
  type TimeZoneOption,
} from '@mangovoice/constants'
import { Field, Toggle } from '@mangovoice/mango-ui'

import type { SystemSettings, SystemSettingsTranslateFn } from '../types'
import { text } from '../types'

const EXTENSION_DIGITS_OPTIONS = [
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
]

export interface GeneralSettingsTabProps {
  settings: SystemSettings
  onSave: (payload: Partial<SystemSettings>) => Promise<void>
  isSaving: boolean
  t?: SystemSettingsTranslateFn
  timeZoneOptions?: TimeZoneOption[]
}

export const GeneralSettingsTab = ({
  settings,
  onSave,
  isSaving,
  t,
  timeZoneOptions,
}: GeneralSettingsTabProps) => {
  const tzOptions = timeZoneOptions ?? EXTENDED_TIMEZONE_OPTIONS
  const normalizedTz = normalizeTimeZone(settings.default_time_zone)

  const [timeZone, setTimeZone] = useState(normalizedTz)
  const [searchQuery, setSearchQuery] = useState('')
  const [callParkTimeout, setCallParkTimeout] = useState(
    settings.call_park_timeout,
  )
  const [extensionDigits, setExtensionDigits] = useState(
    settings.extension_digits,
  )
  const [enableIntercom, setEnableIntercom] = useState(settings.enable_intercom)

  const filteredTimezones = useMemo(() => {
    if (!searchQuery) return tzOptions
    const q = searchQuery.toLowerCase()
    return tzOptions.filter(
      (tz) =>
        tz.label.toLowerCase().includes(q) ||
        tz.value.toLowerCase().includes(q),
    )
  }, [searchQuery, tzOptions])

  const timezoneCollection = useMemo(
    () =>
      createListCollection<TimeZoneOption>({
        items: filteredTimezones,
        itemToValue: (item) => item.value,
        itemToString: (item) => item.label,
      }),
    [filteredTimezones],
  )

  useEffect(() => {
    setTimeZone(normalizeTimeZone(settings.default_time_zone))
    setCallParkTimeout(settings.call_park_timeout)
    setExtensionDigits(settings.extension_digits)
    setEnableIntercom(settings.enable_intercom)
  }, [settings])

  const isDirty =
    timeZone !== normalizedTz ||
    callParkTimeout !== settings.call_park_timeout ||
    extensionDigits !== settings.extension_digits ||
    enableIntercom !== settings.enable_intercom

  const handleSave = useCallback(async () => {
    const timeout = Math.max(30, Math.min(1800, Number(callParkTimeout) || 300))

    if (timeout > 600) {
      const minutes = Math.round(timeout / 60)
      const confirmed = window.confirm(
        text(
          t,
          'systemSettings.general.callParkWarning',
          `Are you sure you want to leave callers in the parking lot for ${minutes} minutes?`,
          { minutes },
        ),
      )
      if (!confirmed) return
    }

    await onSave({
      default_time_zone: timeZone,
      call_park_timeout: String(timeout),
      extension_digits: extensionDigits,
      enable_intercom: enableIntercom,
    })
  }, [timeZone, callParkTimeout, extensionDigits, enableIntercom, onSave, t])

  return (
    <VStack gap={8} align="stretch">
      <Box>
        <Heading fontSize={'xl'} mb={4}>
          {text(t, 'systemSettings.general.title', 'General')}
        </Heading>

        <VStack gap={4} align="stretch">
          <Combobox.Root
            collection={timezoneCollection}
            value={[timeZone]}
            onValueChange={(details) => {
              if (details.value[0]) setTimeZone(details.value[0])
            }}
            inputBehavior="autohighlight"
            onInputValueChange={(details) => setSearchQuery(details.inputValue)}
            openOnClick
            width="100%"
            size="md"
          >
            <Combobox.Label>
              {text(
                t,
                'systemSettings.general.defaultTimeZone',
                'Default Time Zone',
              )}
            </Combobox.Label>
            <Combobox.Control>
              <Combobox.Input
                placeholder={text(
                  t,
                  'systemSettings.general.searchTimezone',
                  'Search timezones...',
                )}
                bg="bg.subtle"
                borderColor="border.default"
              />
              <Combobox.IndicatorGroup>
                <Combobox.Trigger>
                  <ChevronsUpDown />
                </Combobox.Trigger>
              </Combobox.IndicatorGroup>
            </Combobox.Control>
            <Portal>
              <Combobox.Positioner>
                <Combobox.Content maxH="300px" overflowY="auto">
                  <Combobox.Empty>
                    {text(
                      t,
                      'systemSettings.general.noTimezonesFound',
                      'No timezones found',
                    )}
                  </Combobox.Empty>
                  {filteredTimezones.map((tz) => (
                    <Combobox.Item key={tz.value} item={tz}>
                      <Combobox.ItemText>{tz.label}</Combobox.ItemText>
                      <Combobox.ItemIndicator>
                        <Check />
                      </Combobox.ItemIndicator>
                    </Combobox.Item>
                  ))}
                </Combobox.Content>
              </Combobox.Positioner>
            </Portal>
          </Combobox.Root>

          <Field.Root>
            <Field.Label>
              {text(
                t,
                'systemSettings.general.callParkTimeout',
                'Call Park Timeout (seconds)',
              )}
            </Field.Label>
            <Input
              type="number"
              value={callParkTimeout}
              onChange={(e) => setCallParkTimeout(e.target.value)}
              min={30}
              max={1800}
              bg="bg.subtle"
              borderColor="border.default"
            />
            <Field.HelperText>
              {text(
                t,
                'systemSettings.general.callParkTimeoutHelp',
                'Time in seconds a call remains parked before returning. Minimum 30 seconds, maximum 1800 seconds. Recommended: 300.',
              )}
            </Field.HelperText>
          </Field.Root>
        </VStack>
      </Box>

      <Separator />

      <Box>
        <Heading fontSize="lg" mb={4}>
          {text(t, 'systemSettings.general.extensionsTitle', 'Extensions')}
        </Heading>

        <VStack gap={4} align="stretch">
          <Field.Root>
            <Field.Label>
              {text(
                t,
                'systemSettings.general.extensionDigits',
                'Extension Digits',
              )}
            </Field.Label>
            <NativeSelect.Root size="md">
              <NativeSelect.Field
                value={extensionDigits.toString()}
                onChange={(e) =>
                  setExtensionDigits(parseInt(e.target.value, 10))
                }
                bg="bg.subtle"
                borderColor="border.default"
              >
                {EXTENSION_DIGITS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          <Toggle
            checked={enableIntercom}
            onCheckedChange={(details) => setEnableIntercom(details.checked)}
          >
            {text(
              t,
              'systemSettings.general.enableIntercom',
              'Enable Intercom',
            )}
          </Toggle>
        </VStack>
      </Box>

      <HStack gap={3}>
        <Button
          colorPalette="brand"
          onClick={handleSave}
          borderRadius="lg"
          disabled={isSaving || !isDirty}
        >
          {isSaving ? (
            <Spinner size="sm" />
          ) : (
            <Text>{text(t, 'systemSettings.common.save', 'Save Changes')}</Text>
          )}
        </Button>
        {isDirty && (
          <Text fontSize="sm" color="fg.warning" fontWeight="medium">
            {text(t, 'systemSettings.common.unsavedChanges', 'Unsaved changes')}
          </Text>
        )}
      </HStack>
    </VStack>
  )
}
